import { AxiosError, AxiosResponse } from 'axios';
import { defineStore } from 'pinia';
import { Services } from 'src/services/Services';
import { Api } from 'src/services/api/Api';
import {
  ManageBookingConfigurationDTO,
  SearchReservationResponseDTO,
  ErrorResponseDTO,
  SearchAvailabilityRequestDTO,
  RoomTypeDTO,
  RateDTO,
  ReservationItemViewDTO,
  GuestDTO,
  SearchDateOptionsRequestDTO,
  CancelReservationResponseDTO,
  SearchReservationRequestDTO,
  MathsChallengeDTO
} from 'src/services/api/data-contracts';
import { usePropertyStore } from './propertyStore';
import { useServiceStore } from './serviceStore';
import useDateHelperUtils from 'src/composables/dateHelperUtils.ts';
import {
  ManageBookingProcessStep,
  ManageBookingReservation,
  ManageBookingReservationItem,
  ManageBookingDateAvailabilityRow,
} from 'src/types/manageBooking';
import { Notify, Platform } from 'quasar';
import { i18n } from 'boot/i18n';
import {
  IApiDropdownOption,
  Guest,
  IRoom,
  RoomTypeAvailability,
  IReservationItem,
} from 'src/types/common';
import { useRoomTypeAvailabilityStore } from './roomTypeAvailabilityStore';
const t = i18n.global.t;

const apiService = Services.getService(Api);

export const useManageBookingStore = defineStore('manageBookingStore', {
  state: () => ({
    propertyId: null as number | null,
    giftServiceProviderName: null as string | null,
    config: null as ManageBookingConfigurationDTO | null,

    reservationNumber: null as string | null,
    guestName: null as string | null,
    challengeToken: null as string | null,
    challengeAnswer: null as string | null,
    challengeQuestion: null as string | null,

    isSearching: false as boolean,
    hash: null as string,
    searchPerformed: false as boolean,
    searchResponseMessage: null as string | null,

    reservation: null as ManageBookingReservation,
    activeReservationItem: null as ManageBookingReservationItem | null,
    activeGuest: null as Guest | null,

    activeRoom: null as IRoom, // The active room that is being added now

    locationName: null as string,
    locationId: null as number,

    processStep: ManageBookingProcessStep.Start as ManageBookingProcessStep,

    showRoomConfigurationQuestion: false as boolean,

    showCancelBookingForm: false as boolean,
    reservationItemToCancel: null as ManageBookingReservationItem,
    isCancellingBooking: false as boolean,
    requireCancellationReason: false as boolean,
    cancellationReason: null as IApiDropdownOption,

    isSaving: false as boolean,
    saveResponseMessage: null as string,

    isSearchingForNewRooms: false as boolean,

    newDateFrom: null as string,
    newDateTo: null as string,
    hasSearchedForNewDates: false as boolean,
    hasAvailabilityForNewDates: false as boolean,
    isSearchingForNewDates: false as boolean,
    newAvailabilityRows: [] as ManageBookingDateAvailabilityRow[],
  }),
  getters: {
    newTotalCost(): number {
      return this.newAvailabilityRows.reduce((t, y) => {
        return t + y.totalCost;
      }, 0);
    },

    newDateFromDisplay(): string {
      const dateHelperUtils = useDateHelperUtils();
      return dateHelperUtils.formatDateDisplay(
        this.newDateFrom,
        'dddd Do MMMM YYYY',
      );
    },
    newDateToDisplay(): string {
      const dateHelperUtils = useDateHelperUtils();
      return dateHelperUtils.formatDateDisplay(
        this.newDateTo,
        'dddd Do MMMM YYYY',
      );
    },
    newNumberOfNights(): number {
      const dateHelperUtils = useDateHelperUtils();
      return dateHelperUtils.calculateNumberOfNights(
        this.newDateFrom,
        this.newDateTo,
      );
    },

    noAvailabilityForNewDates(): boolean {
      return (
        this.hasSearchedForNewDates &&
        !this.hasAvailabilityForNewDates &&
        !this.isSearchingForNewDates
      );
    },

    canAcceptNewDates(): boolean {
      return (
        this.hasSearchedForNewDates &&
        this.hasAvailabilityForNewDates &&
        !this.isSearchingForNewDates
      );
    },

    availableRoomTypesForActiveRoomSelection(): Array<RoomTypeAvailability> {
      const rtaas = useRoomTypeAvailabilityStore();

      const activeReservationItem = this.activeReservationItem;
      const activeRoom = this.activeRoom;

      const resX = activeReservationItem;

      const roomData: IRoom = activeReservationItem ?? activeRoom;

      const availableRoomTypes =
        rtaas.getAvailableRoomTypesForActiveRoomSelection(
          roomData,
          resX?.RoomTypeId,
          null,
        );

      return availableRoomTypes;
    },

    numberOfRooms(): number {
      return this.reservation.NumberOfRooms;
    },
    numberOfGuests(): number {
      return this.reservation.NumberOfGuests;
    },

    totalRoomCost(): number {
      return this.reservation.TotalAccommodationPrice;
    },
    totalServicesCost(): number {
      return this.reservation.TotalServicesPrice;
    },
    totalCost(): number {
      return (
        this.reservation.TotalAccommodationPrice +
        this.reservation.TotalServicesPrice
      );
    },
    totalVatAmount(): number {
      return this.reservation.TotalVatAmount;
    },

    canSaveReservation(): boolean {
      const hasBooker = this.reservation.Booker != null;
      const bookerIsValid = hasBooker ? this.reservation.Booker.isValid : true;
      const guestsAreValid = this.reservation.ReservationItems.reduce(
        (v, ri) => {
          return v && ri.guestsAreValid;
        },
        true,
      );

      return (
        ((hasBooker && bookerIsValid) || !hasBooker) &&
        guestsAreValid &&
        !this.isSaving
      );
    },
    canShowEditBooking(): boolean {
      return this.reservation.CanBeEdited;
    },
    canShowCancelBooking(): boolean {
      return this.reservation.CanBeCancelled;
    },
    canCancelBooking(): boolean {
      const can = this.reservation.CanBeCancelled;
      let reason = true;
      if (this.requireCancellationReason && this.cancellationReason == null) {
        reason = false;
      }
      return can && reason;
    },

    numberOfNightsDisplay(): string {
      const dateHelperUtils = useDateHelperUtils();
      return dateHelperUtils.formatNumberOfNightsDisplay(
        this.reservation.numberOfNights,
      );
    },
    fromDateDisplay(): string {
      const dateHelperUtils = useDateHelperUtils();
      return dateHelperUtils.formatDateDisplay(this.reservation.DateFrom);
    },
    fromDateFullDisplay(): string {
      const dateHelperUtils = useDateHelperUtils();
      return dateHelperUtils.formatDateDisplay(
        this.reservation.DateFrom,
        'dddd Do MMMM YYYY',
      );
    },
    toDateDisplay(): string {
      const dateHelperUtils = useDateHelperUtils();
      return dateHelperUtils.formatDateDisplay(
        this.reservation.DateTo,
        'dddd, D MMM YYYY',
      );
    },
    roomSummary(): string {
      let roomSummary = '';
      const roomCount = this.numberOfRooms;
      if (roomCount == 1) {
        roomSummary = roomCount + ' ' + t('AccommodationUnitSingular');
      } else if (roomCount > 1) {
        roomSummary = roomCount + ' ' + t('AccommodationUnitPlural');
      }

      return roomSummary;
    },
    guestSummary(): string {
      let guestSummary = '';
      const guestCount = this.numberOfGuests;
      if (guestCount == 1) {
        guestSummary = guestCount + ' ' + t('Guest');
      } else if (guestCount > 1) {
        guestSummary = guestCount + ' ' + t('Guests');
      }

      return guestSummary;
    },
    canSearchBasedOnGuest(): boolean {
      let isValid = false;

      isValid = this.propertyId != undefined;
      isValid =
        isValid &&
        this.reservationNumber != '' &&
        this.reservationNumber != undefined;
      isValid = isValid && this.guestName != '' && this.guestName != undefined;
      isValid = isValid && this.challengeAnswer != '' && this.challengeAnswer != undefined;

      return isValid;
    },

    hasSearchedOnCode(): boolean {
      let isValid = false;

      isValid = this.hash != undefined;
      isValid =
        isValid &&
        this.reservationNumber != '' &&
        this.reservationNumber != undefined;
      isValid = isValid && this.searchPerformed;

      return isValid;
    },
  },
  actions: {
    editReservationItem(
      item: ManageBookingReservationItem,
      processStep: ManageBookingProcessStep,
    ) {
      this.activeReservationItem = item;
      this.setProcessStep(processStep);

      this.activeRoom = item;
    },
    addReservationItem(
      room: IRoom,
      roomType: RoomTypeDTO,
      rate: RateDTO,
    ): ManageBookingReservationItem | null {
      const ps = usePropertyStore();
      const roomTypeAvailabilityStore = useRoomTypeAvailabilityStore();

      const roomTypeQuantityChecker = (roomTypeId: number) => {
        return roomTypeAvailabilityStore.getAvailability(roomTypeId);
      };

      const serviceStore = useServiceStore();

      const guests: GuestDTO[] = [];
      for (let index = 0; index < room.NumberOfAdults; index++) {
        guests.push({
          FirstName: 'New',
          LastName: 'Guest',
          Email: null,
          Phone: null,
        } as GuestDTO);
      }

      const ri = new ManageBookingReservationItem(
        {
          CanBeCancelled: true,
          CanBeEdited: true,
          RatePlanName: rate.RatePlanDescription,
          RoomTypeName: roomType.Name,
          Policy: rate.Policy,
          RoomConfiguration: null,
          ReservationItemId: null,
          DateOfArrival: this.reservation.DateFrom,
          DateOfDeparture: this.reservation.DateTo,
          RoomTypeId: roomType.RoomTypeId,
          RoomConfigurationDropdownOptionId: null,
          RatePlanId: rate.RatePlanId,
          NumberOfAdults: room.NumberOfAdults,
          NumberOfChildren: room.NumberOfChildren,
          NumberOfInfants: room.NumberOfInfants,
          Guests: guests,
          Services: [],
          TotalCost: 0,
        } as ReservationItemViewDTO,
        this.config,
        roomTypeQuantityChecker,
      );

      ri.roomId = room.roomId;
      ri.roomNumber = room.roomNumber;
      ri.Rate = rate;
      ri.VatSettings = ps.vatSettings;

      const availableRoomsForThisRoomType =
        roomTypeAvailabilityStore.getAvailability(roomType.RoomTypeId);

      if (availableRoomsForThisRoomType >= 1) {
        this.reservation.ReservationItems.push(ri);

        roomTypeAvailabilityStore.decreaseAvailabilityByOne(ri.RoomTypeId);

        serviceStore.getNewServices(ri);

        ri.checkForUpgrades();

        return ri;
      } else {
        // Not enough rooms available.
        return null;
      }
    },
    removeReservationItem(reservationItem: ManageBookingReservationItem) {
      const roomTypeAvailabilityStore = useRoomTypeAvailabilityStore();

      const index = this.reservation.ReservationItems.indexOf(reservationItem);
      if (index > -1) {
        this.reservation.ReservationItems.splice(index, 1);

        roomTypeAvailabilityStore.increaseAvailabilityByOne(
          reservationItem.RoomTypeId,
        );
      }
    },

    saveReservation() {
      this.isSaving = true;
      const reservationDto = this.reservation.toDTO();

      const saveReservationPromise =
        apiService.manageBookingUpdate(reservationDto);

      saveReservationPromise.then((saveReservationResponse) => {
        if (!saveReservationResponse.data.Success) {
          this.isSaving = false;

          Notify.create({
            color: 'negative',
            position: 'bottom',
            message: saveReservationResponse.data.Message,
          });
        } else {
          this.saveResponseMessage = saveReservationResponse.data.Message;
          this.setProcessStep(ManageBookingProcessStep.Complete);
          this.isSaving = false;
        }
      });

      saveReservationPromise.catch(
        (preCheckInPromiseAxiosError: AxiosError<ErrorResponseDTO>) => {
          const message =
            preCheckInPromiseAxiosError.response.data?.Message ??
            'Failed to save Reservation';

          Notify.create({
            color: 'negative',
            position: 'bottom',
            message: message,
          });

          this.isSaving = false;
        },
      );

      return saveReservationPromise;
    },
    cancelBooking() {
      this.isCancellingBooking = true;
      const data = {
        PropertyId: this.propertyId,
        ReservationId: this.reservation.ReservationId,
        CancellationReasonId: this.cancellationReason?.Id,
      };
      const promise = apiService.manageBookingCancel(data);

      promise.then((response) => {
        if (response.data.Success) {
          this.isCancellingBooking = false;
          this.showCancelBookingForm = false;
          Notify.create({
            color: 'positive',
            position: 'top',
            message: response.data.Message,
          });
          this.setProcessStep(ManageBookingProcessStep.Cancelled);
        } else if (!response.data.Success) {
          this.isCancellingBooking = false;
          this.showCancelBookingForm = false;
          Notify.create({
            color: 'negative',
            position: 'top',
            message: response.data.Message,
          });
        }
      });

      promise.catch((error: AxiosError<CancelReservationResponseDTO>) => {
        this.isCancellingBooking = false;
        this.showCancelBookingForm = false;

        if (!error.response?.data.Success) {
          Notify.create({
            color: 'negative',
            position: 'top',
            message: error.response?.data.Message,
          });
        }
      });

      return promise;
    },
    dismissCancelBookingForm() {
      this.isCancellingBooking = false;
      this.showCancelBookingForm = false;
      this.reservationItemToCancel = null;
      this.cancellationReason = null;
    },

    next() {
      if (this.processStep == ManageBookingProcessStep.Start) {
        this.processStep = ManageBookingProcessStep.ReservationOverview;
      } else if (this.processStep == ManageBookingProcessStep.Booker) {
        this.processStep = ManageBookingProcessStep.ReservationOverview;
      } else if (this.processStep == ManageBookingProcessStep.Room) {
        let item = null;
        if (
          this.activeReservationItem != null &&
          (!this.activeReservationItem.haveReviewedAllGuests ||
            !this.activeReservationItem.haveReviewedRoom)
        ) {
          item = this.activeReservationItem;
        } else {
          item = this.getNextReservationItem(
            this.activeReservationItem,
            (ri) => {
              return !ri.haveReviewedAllGuests || !ri.haveReviewedRoom;
            },
          );
        }

        this.activeReservationItem = item ?? null;

        if (this.activeReservationItem == null) {
          this.setProcessStep(ManageBookingProcessStep.ReservationOverview);
        } else {
          if (Platform.is.mobile) {
            this.setProcessStep(ManageBookingProcessStep.Guest);
            this.next();
          }
        }
      } else if (
        this.processStep == ManageBookingProcessStep.UpgradeSelection
      ) {
        this.scrollToTop();

        const ari = this.activeReservationItem;
        if (ari != null) {
          ari.checkForUpgrades();
        }

        let item = null;
        if (
          this.activeReservationItem != null &&
          !this.activeReservationItem.haveSeenUpgrades &&
          this.activeReservationItem.hasUpgrades
        ) {
          item = this.activeReservationItem;
        } else {
          item = this.getNextReservationItem(
            this.activeReservationItem,
            (ri) => {
              ri.checkForUpgrades(); // check for upgrades here before selecting.
              return !ri.haveSeenUpgrades && ri.hasUpgrades;
            },
          );
        }

        this.activeReservationItem = item ?? null;
      } else if (this.processStep == ManageBookingProcessStep.Guest) {
        this.scrollToTop();

        let guest = null;
        if (
          this.activeGuest != null &&
          (!this.activeGuest.haveReviewedGuest || !this.activeGuest.isValid)
        ) {
          guest = this.activeGuest;
        } else {
          if (this.activeReservationItem != null) {
            guest = this.activeReservationItem.getNextGuest(
              this.activeGuest,
              (g) => {
                return !g.haveReviewedGuest || !g.isValid;
              },
            );
          }
        }

        this.activeGuest = guest ?? null;

        if (this.activeGuest == null) {
          this.processStep = ManageBookingProcessStep.Room;

          let item = null;
          if (
            this.activeReservationItem != null &&
            (!this.activeReservationItem.haveReviewedAllGuests ||
              !this.activeReservationItem.haveReviewedRoom)
          ) {
            item = this.activeReservationItem;
          } else {
            item = this.getNextReservationItem(
              this.activeReservationItem,
              (ri) => {
                return !ri.haveReviewedAllGuests || !ri.haveReviewedRoom;
              },
            );
          }

          this.activeReservationItem = item ?? null;

          if (this.activeReservationItem == null) {
            this.setProcessStep(ManageBookingProcessStep.ReservationOverview);
          }
        }
      } else if (this.processStep == ManageBookingProcessStep.Extras) {
        this.scrollToTop();
        let item = null;
        if (
          this.activeReservationItem != null &&
          !this.activeReservationItem.hasSelectableServices &&
          !this.activeReservationItem.haveSeenServices
        ) {
          item = this.activeReservationItem;
        } else {
          item = this.getNextReservationItem(
            this.activeReservationItem,
            (ri) => {
              return ri.hasSelectableServices && !ri.haveSeenServices;
            },
          );
        }

        this.activeReservationItem = item ?? null;

        if (this.activeReservationItem == null) {
          this.processStep = ManageBookingProcessStep.Guest;
          this.next();
        }
      } else if (
        this.processStep == ManageBookingProcessStep.ReservationOverview
      ) {
        this.processStep = ManageBookingProcessStep.Summary;
      }
    },

    /// Find a reservation item based on ReservationItemId, with an optional selector
    getReservationItem(
      reservationItemId: number,
      selector: (ri: IReservationItem) => boolean = () => {
        return true;
      },
    ): IReservationItem | null {
      const items = this.reservation.ReservationItems.filter((y) => {
        return y.ReservationItemId == reservationItemId && selector(y);
      });
      if (items.length > 0) {
        return items[0];
      }
      return null;
    },
    getNextReservationItem(
      currentReservationItem: IReservationItem,
      selector: (ri: IReservationItem) => boolean,
    ): IReservationItem | null {
      let nextResX: IReservationItem = null;
      let index = -1;

      const getNextResX = (index: number) => {
        let nextX: IReservationItem = this.reservation.ReservationItems[index];
        if (nextX != null) {
          const resX = this.getReservationItem(
            nextX.ReservationItemId,
            selector,
          );
          if (resX == null) {
            nextX = getNextResX(index + 1);
          } else {
            nextX = resX;
          }
        }

        return nextX;
      };

      if (currentReservationItem == null) {
        index = 0;
        nextResX = getNextResX(index);
      } else {
        index = this.reservation.ReservationItems.findIndex((y) => {
          return (
            y.ReservationItemId == currentReservationItem?.ReservationItemId
          );
        });

        if (index > -1) {
          nextResX = getNextResX(index + 1);
        }
      }

      return nextResX;
    },
    scrollToTop() {
      // TODO Make this mobile vs desktop friendly.
      window.scrollTo(0, 0);
    },
    setProcessStep(step: ManageBookingProcessStep) {
      this.processStep = step;
      if (Platform.is.mobile) {
        this.scrollToTop();
      }
    },

    getManageConfig(propertyId: number) {
      this.propertyId = propertyId;
      const promise = apiService.manageBookingConfig(propertyId);

      promise.then((response) => {
        this.config = response.data;
        this.showRoomConfigurationQuestion =
          response.data.ShowRoomConfigurationQuestion;
        this.requireCancellationReason =
          response.data.RequireCancellationReason;
        this.giftServiceProviderName =
          response.data.GiftServiceProviderConfig.Name;
      });

      return promise;
    },

    resetSearch() {
      this.reservationNumber = null;
      this.guestName = null;
      this.challengeAnswer = null;
      this.challengeToken = null;
      this.challengeQuestion = null;
      this.searchResponseMessage = null;
      this.hash = null;
      this.searchPerformed = false;
      this.getChallenge();
    },

    resetStore() {
      this.reservationNumber = null;
      this.guestName = null;
      this.searchResponseMessage = null;
      this.reservation = null;
      this.cancellationReason = null;
      this.activeGuest = null;
      this.activeReservationItem = null;

      this.setProcessStep(ManageBookingProcessStep.Start);
    },

    getChallenge() {
      const searchChallengePromise = apiService.manageBookingSearchChallenge();
      searchChallengePromise.then(
        (response: AxiosResponse<MathsChallengeDTO>) => {
          this.challengeQuestion = response.data.question;
          this.challengeToken = response.data.token;
          this.challengeAnswer = null;
        }
      );
    },

    searchForReservation(hash: string = null) {
      this.isSearching = true;
      this.searchResponseMessage = null;

      const inputSearch = {
        propertyId: this.propertyId,
        reservationNumber: this.reservationNumber,
        guestName: this.guestName,
        hash: hash,
        challengeAnswer: this.challengeAnswer,
        challengeToken: this.challengeToken
      } as SearchReservationRequestDTO;

      this.hash = hash;

      const searchPromise = apiService.manageBookingSearch(inputSearch);

      searchPromise.then(
        (response: AxiosResponse<SearchReservationResponseDTO>) => {
          const ps = usePropertyStore();
          const serviceStore = useServiceStore();
          const roomTypeAvailabilityStore = useRoomTypeAvailabilityStore();
          this.isSearching = false;
          this.searchPerformed = true;
          this.getChallenge();

          if (!response.data.Success) {
            this.searchResponseMessage = response.data.Message;
          } else {
            const roomTypeQuantityChecker = (roomTypeId: number) => {
              return roomTypeAvailabilityStore.getAvailability(roomTypeId);
            };

            this.reservation = new ManageBookingReservation(
              response.data.Reservation,
              this.config,
              roomTypeQuantityChecker,
              ps.vatSettings,
            );

            this.locationName = response.data.Reservation.LocationName;

            this.reservation.ReservationItems.forEach((ri) => {
              serviceStore.getReservationItemServices(ri);
            });

            if (response.data.Reservation.IsCancelled) {
              this.setProcessStep(ManageBookingProcessStep.Cancelled);
            } else {
              const upgradeQuery = {
                propertyId: this.propertyId,
                reservationId: this.reservation.ReservationId,
              };
              const upgradePromise =
                apiService.manageBookingUpgradeOptions(upgradeQuery);

              upgradePromise.then((upgradeResponse) => {
                if (upgradeResponse.data.RoomTypes.length > 0) {
                  roomTypeAvailabilityStore.initializeAvailableRoomsPerRoomType(
                    upgradeResponse.data.NumberOfRoomsPerRoomTypeId,
                  );

                  this.reservation.ReservationItems.forEach((ri) => {
                    const roomType = upgradeResponse.data.RoomTypes.filter(
                      (rt) => {
                        return rt.RoomType.RoomTypeId == ri.RoomTypeId;
                      },
                    );

                    if (roomType != null && roomType.length > 0) {
                      ri.roomType = roomType[0].RoomType;

                      ri.checkForUpgrades();
                    }
                  });
                }
              });
            }

            this.router.push({
              name: 'Reservation',
              params: {
                propertyCode: ps.propertyCode,
                referenceNumber: response.data.Reservation.ReservationNumber,
                code: response.data.Code,
              },
            });
          }
        },
      );
      searchPromise.catch((error: AxiosError<SearchReservationResponseDTO>) => {
        this.isSearching = false;
        this.searchPerformed = true;
        this.getChallenge();

        if (!error.response?.data.Success) {
          this.searchResponseMessage = error.response?.data.Message;
        }
      });

      return searchPromise;
    },

    searchForNewRoom(newRoom: IRoom) {
      this.activeReservationItem = null;
      this.activeRoom = newRoom;

      this.isSearchingForNewRooms = true;
      const roomTypeAvailabilityStore = useRoomTypeAvailabilityStore();

      const dto: SearchAvailabilityRequestDTO = {
        PropertyId: this.propertyId,
        LanguageCode: 'en',
        PromoCode: null,
        RatePlanCode: null,
        DateFrom: this.reservation.DateFrom,
        DateTo: this.reservation.DateTo,
        LocationId: this.locationId,
        Rooms: [
          {
            NumberOfAdults: newRoom.NumberOfAdults,
            NumberOfChildren: newRoom.NumberOfChildren,
            NumberOfInfants: newRoom.NumberOfInfants,
          },
        ],
      };

      const searchPromise = apiService.availabilitySearch(dto);

      searchPromise.then((searchDataResponse) => {
        roomTypeAvailabilityStore.setRoomTypes(
          searchDataResponse.data.RoomTypes.map(
            (d) => new RoomTypeAvailability(d),
          ),
        );

        roomTypeAvailabilityStore.initializeAvailableRoomsPerRoomType(
          searchDataResponse.data.NumberOfRoomsPerRoomTypeId,
        );
      });

      searchPromise.finally(() => {
        this.isSearchingForNewRooms = false;
      });

      return searchPromise;
    },

    setNewDates(fromDate: string, toDate: string) {
      this.newDateFrom = fromDate;
      this.newDateTo = toDate;
    },

    resetDateChangeSearch() {
      this.newDateFrom = null;
      this.newDateTo = null;
      this.hasSearchedForNewDates = false;
      this.isSearchingForNewDates = false;
      this.hasAvailabilityForNewDates = false;
      this.newAvailabilityRows = [];
    },

    searchForNewDates() {
      this.isSearchingForNewDates = true;
      this.hasAvailabilityForNewDates = false;
      this.newAvailabilityRows = [];

      const dto: SearchDateOptionsRequestDTO = {
        PropertyId: this.propertyId,
        DateFrom: this.newDateFrom,
        DateTo: this.newDateTo,
        ReservationItemIds: this.reservation.ReservationItems.map((ri) => {
          return ri.ReservationItemId;
        }),
      };

      const searchPromise = apiService.manageBookingSearchDateOptions(dto);

      searchPromise.then((searchDataResponse) => {
        this.hasSearchedForNewDates = true;

        if (
          searchDataResponse.data.Success &&
          searchDataResponse.data.ReservationItemRateRows.length > 0
        ) {
          this.hasAvailabilityForNewDates = true;

          searchDataResponse.data.ReservationItemRateRows.forEach((i) => {
            const r = new ManageBookingDateAvailabilityRow(i);
            this.newAvailabilityRows.push(r);
          });
        }
      });

      searchPromise.finally(() => {
        this.isSearchingForNewDates = false;
      });

      return searchPromise;
    },

    applyDateChangeToReservation() {
      this.reservation.ReservationItems.forEach((ri) => {
        ri.DateOfArrival = this.newDateFrom;
        ri.DateOfDeparture = this.newDateTo;

        const riRow = this.newAvailabilityRows.find((i) => {
          return i.ReservationItemId == ri.ReservationItemId;
        });

        if (riRow != null) {
          ri.Rate = riRow;
        }
      });

      this.reservation.DateFrom = this.newDateFrom;
      this.reservation.DateTo = this.newDateTo;

      this.resetDateChangeSearch();
    },
  },
});
